import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import { TBusinessRel, UserProfile } from './types';

export const toggleUser = async (
  dispatch: Dispatch,
  userId: number,
  active: boolean,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/admin/users/${userId}/toggle`,
    dispatch,
    data: { active },
  });
  return response;
};

export const assignSubordinate = async (
  dispatch: Dispatch,
  userId: number,
  subordinates: number[],
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/admin/users/${userId}/subordinate/`,
    dispatch,
    data: { subordinates },
  });
  return response;
};

export const getSubordinates = async (dispatch: Dispatch, userId: number) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/admin/users/${userId}/subordinate/`,
    dispatch,
  });
  return response;
};

export const getUserProfile = (dispatch: Dispatch, id?: number) =>
  useQuery({
    queryKey: ['user', 'profile', id],
    queryFn: async (): Promise<UserProfile | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/users/${id}/profile`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: false,
    enabled: !!id,
    staleTime: 1000 * 60 * 12,
    gcTime: 1000 * 60 * 12,
  });

export const getBusinessRelationsForUser = (dispatch: Dispatch) =>
  useQuery({
    queryKey: ['users', 'businessRelations'],
    queryFn: async (): Promise<TBusinessRel[] | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: 'internal/users/businessRelations',
        dispatch,
      });
      if (response) return response.data.relations;
    },
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

export type AdminUserRelation = {
  id: string;
  businessName: string;
  identifier: string;
  active: boolean;
  userRelationActive: boolean;
  createdAt: string;
  lastExtraction: string;
};

const ADMIN_USER_RELATIONS_KEYS = {
  all: ['adminUserRelations'] as const,
  byUser: (userId: number) => ['adminUserRelations', 'byUser', userId] as const,
};

export const useGetAdminUserRelations = (
  dispatch: Dispatch,
  userId: number,
  enabled = true,
) =>
  useQuery({
    queryKey: ADMIN_USER_RELATIONS_KEYS.byUser(userId),
    queryFn: async (): Promise<AdminUserRelation[] | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/admin/users/${userId}/businessRelations`,
        dispatch,
      });
      if (response) return response.data.relations;
    },
    retry: false,
    enabled,
  });

export const useToggleUserRelationActive = (
  dispatch: Dispatch,
  userId: number,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['adminUserRelations', 'toggle', userId],
    mutationFn: async (data: { businessId: string; active: boolean }) => {
      const response = await requestHandler({
        method: 'patch',
        url: `internal/admin/users/${userId}/businessRelations/toggle`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ADMIN_USER_RELATIONS_KEYS.byUser(userId),
      });
    },
  });
};

export const useDeleteUserRelation = (dispatch: Dispatch, userId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['adminUserRelations', 'delete', userId],
    mutationFn: async (businessId: string) => {
      const response = await requestHandler({
        method: 'delete',
        url: `internal/admin/users/${userId}/businessRelations`,
        dispatch,
        data: { businessId },
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ADMIN_USER_RELATIONS_KEYS.byUser(userId),
      });
    },
  });
};

export const useCreateUserRelation = (dispatch: Dispatch, userId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['adminUserRelations', 'create', userId],
    mutationFn: async (businessId: string) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/admin/users/${userId}/businessRelations`,
        dispatch,
        data: { id: userId, businessId },
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ADMIN_USER_RELATIONS_KEYS.byUser(userId),
      });
    },
  });
};
